import React, { FC, useCallback, useState } from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import LOCAL_STORAGE_KEYS from '@rover/constants/src/search/localStorage.constants';
import { Box, Button, Flex } from '@rover/kibble/core';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import useIsDefaultLocale from '@rover/react-lib/src/pages/index/HomePage/hooks/useIsDefaultLocale';
import buildSearchEvent from '@rover/react-lib/src/pages/index/HomePage/utilities/buildSearchEvent';
import type { FrontendConfigurationSettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/frontendConfigurationSettingsDogSizeBuckets';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useFlag from '@rover/shared/js/hooks/useFlag';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';
import type { HomepageSearchFilters } from '@rover/types/src/HomepageSearchFilters';
import { Pet } from '@rover/types/src/Pet';
import type { ServiceTypeOption } from '@rover/types/src/ServiceType';
import { ServiceType, ServiceTypeSlug } from '@rover/types/src/ServiceType';
import fireCrossPageEvent from '@rover/utilities/fireCrossPageEvent';

import { HomepageCtaClickEvent } from '../../utilities/analyticsEvents';

import HomePageSearchBoxLocationInput from './connectors/LocationInputConnector';
import ServiceTypeConnector from './connectors/ServiceTypeConnector';
import SearchBoxHiddenInputs from './SearchBoxHiddenInputs';
import type { UpdateFiltersFunc } from './UpdateFiltersFunc';

export interface Props {
  dateRangePickerLabel: string;
  endDatePlaceholder?: string;
  filters: HomepageSearchFilters;
  locale: Locale;
  pets: Pet[];
  petSizeBuckets: FrontendConfigurationSettingsDogSizeBuckets;
  serviceType: ServiceType | undefined;
  serviceTypeOptions: ServiceTypeOption[];
  startDatePlaceholder?: string;
  updateFilters: UpdateFiltersFunc;
  weightUnit: string;
  className?: string;
}

const ctaText = getBrandSpecificValue<MessageDescriptor>({
  rover: msg`Search`,
});

const getStartedCtaText = getBrandSpecificValue<MessageDescriptor>({
  rover: msg`Get Started`,
});

const SearchBox: FC<Props> = (props: Props) => {
  const { filters, serviceTypeOptions } = props;

  const { i18n } = useLingui();
  const { setScreenReaderAnnouncement } = useA11yContext();

  // Sticky interaction flag: once the user touches any field it stays true for
  // the component's lifetime, even if the field is later cleared.
  const [hasInteracted, setHasInteracted] = useState(false);
  const markInteraction = useCallback(() => setHasInteracted(true), []);

  const isInGeoBrowseExperiment = useFlag(FLAGS.GEO_BROWSE_HOMEPAGE_ENTRYPOINT);
  const { isAuthenticated } = useCurrentUserShim();
  // The geo-browse entrypoint is only translated for each country's default
  // locale, so restrict it to that locale and fall back to Search otherwise.
  const isDefaultLocale = useIsDefaultLocale();

  const isTraining = filters.serviceTypeSlug === ServiceTypeSlug.DOG_TRAINING;

  // Route unauthenticated, not-yet-interacted users to the /get-started/
  // interstitial when the geo browse gate is enabled.
  const action = (() => {
    if (isTraining) return '/training/search/';
    if (isInGeoBrowseExperiment && !isAuthenticated && !hasInteracted && isDefaultLocale) {
      return '/get-started/';
    }
    return '/search/';
  })();
  const routesToGetStarted = action === '/get-started/';

  // Native GET form submission: do NOT preventDefault — let the browser navigate.
  const handleSubmit = useCallback(() => {
    new HomepageCtaClickEvent().fireCrossPageEvent();
    setScreenReaderAnnouncement(t`Form submitted. Navigating to Search page.`);
    fireCrossPageEvent(LOCAL_STORAGE_KEYS.HOMEPAGE_SEARCH_EVENT, buildSearchEvent(filters));
  }, [filters, setScreenReaderAnnouncement]);

  const areServicesLoaded = serviceTypeOptions?.length > 0;

  return (
    <Box
      className={props.className}
      background="primary"
      width={[768, 768, 850]}
      data-qa-id="homepage-search-box-container"
      data-testid="homepage-search-box-container"
      borderRadius="secondary"
    >
      <Box
        height="100%"
        p={['2x', '2x', '4x']}
        mt="0x"
        minHeight={areServicesLoaded ? undefined : '314px'}
      >
        {areServicesLoaded && (
          <>
            {/* ServiceTypeConnector and the location input live OUTSIDE the form so their
                interactive controls (e.g. SelectButton's value-less radios) are not submitted
                as stray params. The form carries state via SearchBoxHiddenInputs instead. */}
            <ServiceTypeConnector {...props} onInteract={markInteraction} />
            <Box>
              <Flex alignItems="flex-end" justifyContent="space-between" mb="3x">
                <Box flex="1" sx={{ maxWidth: '300px' }} mr="3x">
                  <HomePageSearchBoxLocationInput {...props} onInteract={markInteraction} />
                </Box>
                <Box flex="1" sx={{ maxWidth: '250px' }}>
                  <form method="GET" action={action} onSubmit={handleSubmit}>
                    {/* The Get Started interstitial should be reached with a clean URL, so
                        omit the hidden inputs that would otherwise be appended as query params. */}
                    {!routesToGetStarted && (
                      <SearchBoxHiddenInputs
                        filters={filters}
                        spacesRequired={1}
                        isDogTraining={isTraining}
                      />
                    )}
                    <Button
                      data-testid="search-box-submit"
                      size="default"
                      fullWidth
                      variant="primary"
                      webProps={{ type: 'submit' }}
                    >
                      {i18n._(routesToGetStarted ? getStartedCtaText : ctaText)}
                    </Button>
                  </form>
                </Box>
              </Flex>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SearchBox;
